import { Routes } from '@packages/enums';
import { Icon } from '@ui/assets/icons/IconWrapper';
import { Button } from '@ui/components/button';
import { Modal } from 'antd';
import type { IPatient } from './PatientListSelection';
import type { PatientResponse } from '@packages/types';

interface PatientDetailsModalProps {
    open: boolean;
    onClose: () => void;
    patient: IPatient | PatientResponse | undefined;
}

export default function PatientDetailsModal({ open, onClose, patient }: PatientDetailsModalProps) {
    if (!patient) return null;

    return (
        <Modal
            open={open}
            onCancel={onClose}
            footer={null}
            width={600}
        >
            <div className="flex min-h-[400px] flex-col items-center justify-between p-[20px] font-medium">
                <div className="flex w-full flex-col items-center gap-[20px]">
                    <div className="mb-[16px] flex flex-col items-center gap-[8px]">
                        <img
                            src="/blank_profile.png"
                            width={75}
                            height={75}
                            className="rounded-[20px]"
                            alt="Patient profile"
                        />
                        <div className="flex flex-col items-center gap-[4px]">
                            <span className="text-[20px] font-semibold">{patient.name || 'Unknown'}</span>
                            <span className="text-[16px] font-normal text-[#565656]">
                                Patient ID : {patient.omopPersonId ?? patient.id}
                            </span>
                        </div>
                    </div>
                    <div className="grid w-full grid-cols-2 gap-[20px]">
                        {(
                            [
                                { key: 'Gender', icon: 'Gender', unit: undefined, value: patient.gender },
                                { key: 'Weight', icon: 'Weight', unit: 'kg', value: undefined },
                                { key: 'Age', icon: 'Age', unit: undefined, value: patient.age },
                                { key: 'Height', icon: 'Height', unit: 'cm', value: undefined },
                            ] as const
                        ).map((item, index) => (
                            <div
                                key={index}
                                className={`flex items-center gap-[4px] ${index % 2 !== 0 ? 'justify-start' : 'justify-end'}`}
                            >
                                <div className="flex items-center justify-end gap-[4px]">
                                    <Icon
                                        name={item.icon}
                                        size={20}
                                    />
                                    <span className="text-[14px] font-normal leading-[18px] text-[#808080]">
                                        {item.key}
                                    </span>
                                </div>
                                <div className="flex items-center justify-start">
                                    <span className="text-[14px] font-semibold leading-[18px] text-[#181818]">
                                        {item.value ?? '-'} {item.value ? item.unit : ''}
                                    </span>
                                </div>
                            </div>
                        ))}
                    </div>
                    <div className="mt-[20px] grid w-full grid-cols-2 gap-[8px]">
                        {[
                            {
                                title: 'Doc. Number',
                                result: patient.documentCount ?? '-',
                                colSpan: 1,
                            },
                            {
                                title: 'Primary Diagnosis',
                                result: patient.diagnosis_list?.[0]?.name ?? '-',
                                colSpan: 1,
                            },
                            {
                                title: 'Ethnicity',
                                result: patient.ethnicity ?? '-',
                                colSpan: 1,
                            },
                        ].map((item, index) => (
                            <div
                                key={index}
                                className="flex flex-col items-center gap-[4px] rounded-[8px] bg-[#F8F8F8] p-[12px]"
                                style={{ gridColumn: `span ${item.colSpan || 1}` }}
                            >
                                <div className="flex flex-col gap-[2px]">
                                    <span className="text-[12px] font-medium leading-[14px] text-[#262626]">
                                        {item.title}
                                    </span>
                                </div>
                                <span className="text-[14px] font-semibold leading-[18px] text-[#313131]">
                                    {String(item.result)}
                                </span>
                            </div>
                        ))}
                    </div>
                    <Button
                        iconPosition="end"
                        icon={
                            <Icon
                                name="UpperRightArrow"
                                size={20}
                            />
                        }
                        type="default"
                        onClick={() => {
                            if (patient?.omopPersonId || patient?.id) {
                                window.open(`${Routes.PATIENTS}/${patient.omopPersonId ?? patient.id}`, '_blank');
                            }
                            onClose();
                        }}
                    >
                        View Detailed Patient Profile
                    </Button>
                </div>
            </div>
        </Modal>
    );
}
